import React, { useState, useEffect } from 'react';
import {
  TrendingUp, Users, CheckCircle2, XCircle, Star, Sparkles,
  Clock, Briefcase, Activity, Inbox, RefreshCw, Loader2,
} from 'lucide-react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const FUNNEL_STAGES = [
  { key: 'new',         label: 'New',         color: '#00E5FF' },
  { key: 'reviewed',    label: 'Reviewed',    color: '#F59E0B' },
  { key: 'shortlisted', label: 'Shortlisted', color: '#c39be5' },
  { key: 'hired',       label: 'Hired',       color: '#10B981' },
  { key: 'rejected',    label: 'Rejected',    color: '#EF4444' },
];

const VacancyAnalyticsPage = ({ getAuthHeaders, currentUser }) => {
  const isAllowed = currentUser?.role === 'superadmin' || currentUser?.role === 'executive';
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${BACKEND_URL}/api/admin/vacancy-analytics`, { headers: getAuthHeaders() });
      setData(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (isAllowed) load(); /* eslint-disable-next-line */ }, []);

  if (!isAllowed) {
    return (
      <div className="flex items-center justify-center h-[60vh]" data-testid="analytics-denied">
        <div className="text-center">
          <TrendingUp className="w-12 h-12 text-amber-400 mx-auto mb-3" />
          <h2 className="text-xl font-light text-white tracking-tight mb-1">Restricted</h2>
          <p className="text-white/40 text-sm">Only executives and superadmins can view recruitment analytics.</p>
        </div>
      </div>
    );
  }

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center py-24" data-testid="analytics-loading">
        <Loader2 className="w-6 h-6 text-white/40 animate-spin" />
      </div>
    );
  }

  if (!data) return null;

  const t = data.totals || {};
  const funnel = data.funnel || {};
  const perVac = data.per_vacancy || [];
  const tth = data.time_to_hire || {};
  const daily = data.daily_activity || [];
  const funnelMax = Math.max(1, ...FUNNEL_STAGES.map((s) => funnel[s.key] || 0));
  const dailyMax = Math.max(1, ...daily.map((d) => d.count));
  const hiredPct = t.applications ? Math.round((t.hired / t.applications) * 1000) / 10 : 0;

  return (
    <div className="space-y-4 pb-8" data-testid="analytics-panel">
      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <KPI icon={Inbox}       label="Applications"    value={t.applications} tone="cyan" />
        <KPI icon={Star}        label="Shortlisted"     value={t.shortlisted}  tone="violet" />
        <KPI icon={CheckCircle2} label="Hired"          value={t.hired}         tone="emerald" hint={`${hiredPct}% conversion`} />
        <KPI icon={Sparkles}    label="Talent Pool"     value={t.talent_pool}  tone="amber" />
        <KPI icon={Clock}       label="Avg time-to-hire" value={tth.count ? `${tth.avg_days}d` : '—'} tone="cyan" hint={tth.count ? `${tth.count} hires · median ${tth.median_days}d` : 'No hires yet'} />
      </div>

      {/* Funnel + 30-day activity */}
      <div className="grid md:grid-cols-2 gap-3">
        <div className="rounded-2xl border border-white/[0.06] bg-[#0A0A12] p-5" data-testid="funnel-card">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <TrendingUp className="w-3.5 h-3.5 text-[#00E5FF]" />
              <h3 className="text-white text-sm font-medium tracking-tight">Funnel</h3>
            </div>
            <Button size="sm" variant="ghost" onClick={load} disabled={loading} data-testid="analytics-refresh"
              className="h-8 w-8 p-0 rounded-full text-white/40 hover:text-white">
              <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
          <div className="space-y-2.5">
            {FUNNEL_STAGES.map((s) => {
              const v = funnel[s.key] || 0;
              const pct = Math.round((v / funnelMax) * 100);
              return (
                <div key={s.key} data-testid={`funnel-${s.key}`}>
                  <div className="flex items-center justify-between text-[11px] mb-1">
                    <span className="text-white/60 uppercase tracking-[0.1em] flex items-center gap-1.5">
                      <span className="w-1.5 h-1.5 rounded-full" style={{ background: s.color }} />
                      {s.label}
                    </span>
                    <span className="tabular-nums text-white/80 font-medium">{v}</span>
                  </div>
                  <div className="w-full h-1.5 bg-white/[0.04] rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all duration-500"
                      style={{ width: `${pct}%`, background: s.color, boxShadow: `0 0 10px ${s.color}66` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="rounded-2xl border border-white/[0.06] bg-[#0A0A12] p-5" data-testid="daily-card">
          <div className="flex items-center gap-2 mb-4">
            <Activity className="w-3.5 h-3.5 text-[#c39be5]" />
            <h3 className="text-white text-sm font-medium tracking-tight">Last 30 days</h3>
            <span className="ml-auto text-white/40 text-[11px] tabular-nums">{daily.reduce((a, d) => a + d.count, 0)} applications</span>
          </div>
          <div className="flex items-end gap-0.5 h-32">
            {daily.map((d, i) => {
              const h = Math.max(2, Math.round((d.count / dailyMax) * 110));
              return (
                <div key={i} className="flex-1 group relative flex flex-col justify-end" title={`${d.date}: ${d.count}`}>
                  <div
                    className="w-full rounded-t-sm bg-gradient-to-t from-[#8A2BE2]/40 to-[#00E5FF]/60 hover:from-[#8A2BE2]/60 hover:to-[#00E5FF]/90 transition-all"
                    style={{ height: `${h}px` }}
                  />
                </div>
              );
            })}
          </div>
          <div className="flex justify-between text-[9px] text-white/25 mt-2 tabular-nums">
            <span>{daily[0]?.date?.slice(5)}</span>
            <span>{daily[Math.floor(daily.length / 2)]?.date?.slice(5)}</span>
            <span>Today</span>
          </div>
        </div>
      </div>

      {/* Per-vacancy table */}
      <div className="rounded-2xl border border-white/[0.06] bg-[#0A0A12] overflow-hidden" data-testid="per-vacancy-card">
        <div className="px-5 py-4 border-b border-white/[0.05] flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Briefcase className="w-3.5 h-3.5 text-emerald-300" />
            <h3 className="text-white text-sm font-medium tracking-tight">Applicants per vacancy</h3>
          </div>
          <span className="text-white/40 text-[11px] tabular-nums">{perVac.length} roles</span>
        </div>
        {perVac.length === 0 ? (
          <div className="px-5 py-10 text-center text-white/40 text-sm">No applications received yet.</div>
        ) : (
          <div className="divide-y divide-white/[0.04]">
            {perVac.map((v) => {
              const conv = v.total ? Math.round((v.hired / v.total) * 1000) / 10 : 0;
              return (
                <div key={v.vacancy_id} className="px-5 py-4 grid grid-cols-12 items-center gap-3" data-testid={`per-vacancy-row-${v.vacancy_id}`}>
                  <div className="col-span-12 md:col-span-4 min-w-0">
                    <p className="text-white text-sm font-medium truncate">{v.vacancy_title}</p>
                    <p className="text-white/30 text-[10px] mt-0.5 tabular-nums">{v.total} applicants · {conv}% conversion</p>
                  </div>
                  <div className="col-span-12 md:col-span-8">
                    <div className="flex h-2 rounded-full overflow-hidden bg-white/[0.04]">
                      {FUNNEL_STAGES.map((s) => {
                        const val = v[s.key] || 0;
                        const pct = v.total ? (val / v.total) * 100 : 0;
                        if (!pct) return null;
                        return (
                          <div key={s.key} title={`${s.label}: ${val}`}
                            style={{ width: `${pct}%`, background: s.color }} />
                        );
                      })}
                    </div>
                    <div className="flex flex-wrap gap-x-3 gap-y-1 mt-2 text-[10px]">
                      {FUNNEL_STAGES.map((s) => v[s.key] > 0 && (
                        <span key={s.key} className="tabular-nums text-white/50 inline-flex items-center gap-1">
                          <span className="w-1.5 h-1.5 rounded-full" style={{ background: s.color }} />
                          {s.label} <span className="text-white/80 font-medium">{v[s.key]}</span>
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

const TONE_STYLES = {
  cyan:    { bg: 'from-[#00E5FF]/[0.10] to-transparent',  border: 'border-[#00E5FF]/20',  icon: 'text-[#00E5FF]' },
  violet:  { bg: 'from-[#8A2BE2]/[0.10] to-transparent',  border: 'border-[#8A2BE2]/25',  icon: 'text-[#c39be5]' },
  emerald: { bg: 'from-emerald-500/[0.10] to-transparent', border: 'border-emerald-500/25', icon: 'text-emerald-300' },
  amber:   { bg: 'from-amber-500/[0.10] to-transparent',   border: 'border-amber-500/25',   icon: 'text-amber-300' },
};

const KPI = ({ icon: Icon, label, value, tone = 'cyan', hint }) => {
  const s = TONE_STYLES[tone] || TONE_STYLES.cyan;
  return (
    <div className={`rounded-2xl border ${s.border} bg-gradient-to-br ${s.bg} p-4 relative overflow-hidden`}>
      <div className="flex items-start justify-between mb-2">
        <p className="text-[10px] tracking-[0.15em] uppercase text-white/40 font-medium">{label}</p>
        <Icon className={`w-3.5 h-3.5 ${s.icon}`} strokeWidth={1.75} />
      </div>
      <p className="text-white text-2xl font-light tracking-tight tabular-nums">{value ?? '—'}</p>
      {hint && <p className="text-white/30 text-[10px] mt-1 tabular-nums">{hint}</p>}
    </div>
  );
};

export default VacancyAnalyticsPage;
